import React from 'react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';

const ResponseRateChart = ({ campaigns }) => {
  // Process campaign data for the chart
  const processData = () => {
    if (!campaigns || campaigns.length === 0) {
      return [];
    }

    // Create a map of months with response rates
    const monthlyData = {};
    const now = new Date();
    
    // Get data for the last 6 months
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
      
      monthlyData[monthKey] = {
        month: monthKey,
        displayMonth: date.toLocaleString('default', { month: 'short', year: 'numeric' }),
        totalContacts: 0,
        respondedContacts: 0,
        responseRate: 0,
        campaigns: 0
      };
    }
    
    // Process each campaign
    campaigns.forEach(campaign => {
      // Skip campaigns without start dates
      if (!campaign.startDate) return;
      
      const startDate = new Date(campaign.startDate);
      const monthKey = `${startDate.getFullYear()}-${(startDate.getMonth() + 1).toString().padStart(2, '0')}`;
      
      // Only include campaigns from the last 6 months
      if (!monthlyData[monthKey]) return;
      
      // Get contacts for this campaign
      const campaignContacts = JSON.parse(localStorage.getItem(`campaign_contacts_${campaign.id}`)) || [];
      
      // Update monthly data
      monthlyData[monthKey].totalContacts += campaignContacts.length;
      monthlyData[monthKey].respondedContacts += campaignContacts.filter(
        c => c.status === 'responded' || c.status === 'converted'
      ).length;
      monthlyData[monthKey].campaigns += 1;
    });
    
    // Calculate response rates and convert to array
    return Object.values(monthlyData).map(month => {
      const responseRate = month.totalContacts > 0 
        ? (month.respondedContacts / month.totalContacts) * 100 
        : 0;
        
      return {
        ...month,
        responseRate: parseFloat(responseRate.toFixed(1))
      };
    });
  };

  const data = processData();
  
  // Calculate average response rate
  const averageResponseRate = data.length > 0
    ? parseFloat((data.reduce((sum, item) => sum + item.responseRate, 0) / data.length).toFixed(1))
    : 0;

  return (
    <div className="h-96">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={data}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 20,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="displayMonth" />
          <YAxis 
            label={{ value: 'Response Rate (%)', angle: -90, position: 'insideLeft' }}
            domain={[0, 100]}
          />
          <Tooltip 
            formatter={(value, name) => {
              if (name === 'responseRate') return [`${value}%`, 'Response Rate'];
              if (name === 'totalContacts') return [value, 'Total Contacts'];
              return [value, name];
            }}
          />
          <Legend />
          <ReferenceLine 
            y={averageResponseRate} 
            label={`Avg: ${averageResponseRate}%`} 
            stroke="#718096" 
            strokeDasharray="3 3" 
          />
          <Line 
            type="monotone" 
            dataKey="responseRate" 
            name="Response Rate" 
            stroke="#3182CE" 
            activeDot={{ r: 8 }} 
            strokeWidth={2}
          />
          <Line 
            type="monotone" 
            dataKey="totalContacts" 
            name="Total Contacts" 
            stroke="#CBD5E0" 
            strokeDasharray="5 5"
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ResponseRateChart;
